import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const PHPCertification = () => {
  const testimonials = [
    {
      id: 1,
      name: "Sagar Singh",
      review:
        "The PHP certification course at Nadezhda Venture is simply the best! I learned so much about backend development, database integration, and even Laravel. The instructors are experts and always ready to help. Highly recommended for anyone serious about a career in web development.",
      rating: 5,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/men/33.jpg", // Placeholder image
    },
    {
      id: 2,
      name: "Kavita Devi",
      review:
        "I gained immense confidence in PHP and MySQL after completing my training here. The practical approach, real-time projects, and consistent doubt clearing sessions were invaluable. The certification opened doors to great opportunities.",
      rating: 4,
      date: "2024-07-20",
      profileImg: "https://randomuser.me/api/portraits/women/45.jpg", // Placeholder image
    },
    {
      id: 3,
      name: "Rohit Verma",
      review:
        "This is an excellent institute for PHP training. The curriculum is comprehensive, covering core PHP to advanced frameworks. The mentors provide great support and help you understand complex concepts easily. A fantastic place to learn and grow!",
      rating: 5,
      date: "2024-07-18",
      profileImg: "https://randomuser.me/api/portraits/men/51.jpg", // Placeholder image
    },
    {
      id: 4,
      name: "Pooja Sharma",
      review:
        "Nadezhda Venture made learning PHP enjoyable and effective. The structured modules, interactive sessions, and practical assignments ensured comprehensive learning. I'm now a confident PHP developer, all thanks to their guidance.",
      rating: 4,
      date: "2024-07-15",
      profileImg: "https://randomuser.me/api/portraits/women/62.jpg", // Placeholder image
    },
    {
      id: 5,
      name: "Vivek Kumar",
      review:
        "Very good platform for mastering PHP. The course was well-explained, and the instructors were very supportive. I gained a lot of practical knowledge, especially in database management and framework usage.",
      rating: 4,
      date: "2024-07-10",
      profileImg: "https://randomuser.me/api/portraits/men/78.jpg", // Placeholder image
    },
  ];
   const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is PHP and why is it important for web development?",
      answer:
        "PHP (Hypertext Preprocessor) is a popular open-source scripting language especially suited for web development. It's used for server-side programming, enabling dynamic content, database interaction, and handling forms. Its wide adoption, robust frameworks (like Laravel, CodeIgniter), and strong community make it crucial for building versatile web applications and content management systems like WordPress.",
    },
    {
      title: "Core PHP Concepts: What will I learn in basic PHP?",
      answer:
        "In Core PHP, you'll learn fundamentals like syntax, data types, variables, operators, conditional statements (if/else, switch), loops (for, while, foreach), functions, and arrays. This forms the strong foundation necessary for any advanced PHP development.",
    },
    {
      title: "Object-Oriented PHP (OOP): How is OOP applied in PHP?",
      answer:
        "Object-Oriented Programming in PHP involves concepts like classes, objects, inheritance, encapsulation, polymorphism, and abstraction. Learning OOP in PHP helps in writing modular, reusable, and maintainable code, essential for large-scale applications and framework development.",
    },
    {
      title: "Database Connectivity: How does PHP interact with databases?",
      answer:
        "PHP primarily interacts with databases like MySQL using extensions such as MySQLi or PDO (PHP Data Objects). You will learn to connect to databases, perform CRUD (Create, Read, Update, Delete) operations, write SQL queries, and ensure secure data handling, which is fundamental for dynamic web applications.",
    },
    {
      title: "PHP Frameworks: Which frameworks are covered in the course?",
      answer:
        "Our PHP certification course covers popular and in-demand frameworks. We primarily focus on **Laravel**, one of the most powerful and widely used PHP frameworks, known for its elegant syntax and robust features. Depending on the batch and curriculum, other frameworks like CodeIgniter might also be introduced.",
    },
    {
      title: "What about CMS development like WordPress?",
      answer:
        "Yes, understanding Content Management Systems (CMS) is a crucial part of modern web development. Our course includes an introduction to WordPress, covering topics like theme customization, plugin development, and best practices for managing WordPress sites, leveraging your PHP skills.",
    },
    {
      title: "What kind of projects will I work on during the certification?",
      answer:
        "You'll build several hands-on projects, including a dynamic website, an e-commerce platform backend, and potentially a mini-CMS or API using Laravel. These projects will solidify your understanding and help you build a strong portfolio.",
    },
    {
      title: "How does the PHP Certification process work?",
      answer:
        "Upon successfully completing all course modules, practical assignments, and a final project assessment, you will be awarded a **PHP Development Certification** from Nadezhda Venture. This certificate demonstrates your readiness for professional PHP development roles.",
    },
    {
      title: "Is placement assistance provided after certification?",
      answer:
        "Absolutely. We offer dedicated placement support, which includes resume building, mock interview preparation, and connecting you with our network of hiring companies in Noida and beyond, helping you secure suitable job opportunities as a PHP Developer.",
    },
    {
      title: "What career paths can I pursue with PHP Certification?",
      answer:
        "A PHP certification can lead to roles like PHP Developer, Backend Developer, Web Developer, Laravel Developer, WordPress Developer, and Junior Full-Stack Developer. PHP remains highly relevant for enterprise applications and CMS-driven websites.",
    },
  ];

  const certificationDetails = [
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
      ),
      title: "Globally Recognized Certification",
      description:
        "Earn a certificate that validates your expertise and boosts your career prospects in web development.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          ></path>
        </svg>
      ),
      title: "Practical Project Experience",
      description:
        "Develop a robust portfolio by working on multiple real-world PHP projects, including Laravel.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          ></path>
        </svg>
      ),
      title: "Comprehensive PHP & MySQL",
      description:
        "Master Core PHP, OOP, MySQL database management, and advanced web development techniques.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8c1.657 0 3 .895 3 2s-1.343 2-3 2-3-.895-3-2 1.343-2 3-2zM21 12c0 8-9 16-9 16S3 20 3 12a9 9 0 1118 0z"
          ></path>
        </svg>
      ),
      title: "Strong Placement Support",
      description:
        "Benefit from our robust placement assistance and connect with leading companies hiring PHP developers.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
          ></path>
        </svg>
      ),
      title: "Expert-Led Training",
      description:
        "Learn from industry professionals with extensive experience in PHP and web application development.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7m-4 5h-4m4 0H9m7 0H9m7 0a2 2 0 002-2V9a2 2 0 00-2-2h-4a2 2 0 00-2 2v3"
          ></path>
        </svg>
      ),
      title: "Laravel & WordPress Integration",
      description:
        "Gain hands-on skills in popular PHP frameworks and CMS platforms for comprehensive development.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  // Determine how many testimonials to show at once (e.g., 1 on small, 2 on medium, 3 on large)
  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const endIndex =
    startIndex + visibleCount > testimonials.length
      ? testimonials.length
      : startIndex + visibleCount;
  const currentVisibleTestimonials = [];

  for (let i = startIndex; i < endIndex; i++) {
    currentVisibleTestimonials.push(testimonials[i]);
  }

  // Handle wrapping around for fewer than visibleCount items at the end
  if (
    currentVisibleTestimonials.length < visibleCount &&
    testimonials.length >= visibleCount
  ) {
    const remaining = visibleCount - currentVisibleTestimonials.length;
    for (let i = 0; i < remaining; i++) {
      currentVisibleTestimonials.push(testimonials[i]);
    }
  } else if (testimonials.length < visibleCount) {
    // If total testimonials are less than visible count, just show all
    currentVisibleTestimonials.push(...testimonials);
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>PHP Certification Training in Noida | Laravel & MySQL Course</title>
        <meta
          name="description"
          content="Get certified in PHP development in Noida. Our course covers Core PHP, OOP, MySQL, Laravel, and WordPress with live projects and 100% placement assistance."
        />
        <meta
          name="keywords"
          content="PHP training Noida, PHP certification, Laravel training, MySQL course, Web development with PHP, WordPress development, PHP placement Noida"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://t3.ftcdn.net/jpg/05/57/10/42/360_F_557104220_p1cZuUgzqM64rogJ6q1IscMQuyamWTGm.jpg"
          alt="PHP Development Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              <span className="text-indigo-400">PHP</span> Certification Training in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Master backend development with our comprehensive PHP certification program. Learn Core PHP, MySQL, Laravel, and build dynamic web applications.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
             <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("PHP Certification Course")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Certification Highlights Section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-900">
            Why Choose Our PHP Certification?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certificationDetails.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center"
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-700">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Content / FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">
            Frequently Asked Questions (FAQ) & Course Curriculum
          </h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-48">
          <div className="bg-blue-300 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our PHP certification program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., PHP Certification)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                GET CERTIFIED
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-900">
            Our Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id} // Key by testimonial ID for consistent animation
                    initial={{ opacity: 0, x: idx * 100 }} // Initial position based on index in visible set
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                    // w-[calc(50%-1.5rem)] means 50% width minus half of the space-x-6 (3rem total gap)
                    // w-[calc(33.333%-2rem)] means 33.333% width minus two-thirds of the space-x-6 (4rem total gap)
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-blue-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-blue-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  // Logic to highlight dots based on the current set of visible testimonials
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-blue-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Conditionally render the popup */}
            {isPopupOpen && (
              <JoinNowFormPopup
                onClose={handleClosePopup}
                courseTitle={selectedCourseTitle}
              />
            )}
    </div>
  );
};

export default PHPCertification;